import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, Input, ViewChild, inject, HostListener, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { TreeNode, PrimeTemplate } from 'primeng/api';
import { cloneDeep } from 'lodash';
import { TreeTable, TreeTableModule } from 'primeng/treetable';
import { HisCoreSharedModule } from '../../../../../../core/his-core-shared.module';
import { HisNotificationService } from '../../../../../../core/service/notifications/his-notification.service';
import { NgClass, DecimalPipe } from '@angular/common';
import { Column } from '../../flat-table/model/column';
import { ColumnType } from '../../flat-table/model/column-type';
import { Result } from '../../flat-table/model/result';
import { TableHeader } from '../../flat-table/model/table-header';
import { TableToolbarComponent } from '../table-toolbar/table-toolbar.component';
import { MinuteSecondPipePipe } from '../pipe/minute-second-pipe.pipe';
import { Row } from '../../flat-table/model/row';


@Component( {
    standalone: true,
    selector: 'his-bi-smart-treetable',
    templateUrl: './smart-treetable.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TableToolbarComponent, TreeTableModule, HisCoreSharedModule, PrimeTemplate, NgClass, DecimalPipe, MinuteSecondPipePipe]
} )
export class SmartTreeTableComponent implements AfterViewInit, OnChanges {

    private notificationService = inject( HisNotificationService );
    private cdRef = inject( ChangeDetectorRef );
    private isCtrlPressed: boolean = false;
    rowsCount: number = 10;
    rowsPerPageOptions = [10, 20, 50];


    @Output() readonly leftDimensionAttributeChange = new EventEmitter<string[]>();


    @Input()
    set result( result: Result ) {
        this.handleNewResult( result );
    }

    @Input() headerOrder: string[] = [];
    @Input() totalColumn: number = 0;

    @Input() readOnly: boolean = false;


    flatResult!: Result;
    private _treeResult: TreeNode[] = [];

    get treeResult(): TreeNode[] {
        return this._treeResult;
    }

    get groupableColCount(): number {
        return this.cols.filter( c => c.groupable === true ).length;
    }

    set treeResult( theTreeResult: TreeNode[] ) {
        this.rowsExpandedCount = this.countExpandedNodes( theTreeResult );
        if ( this.rowsExpandedCount > 1000 && this.maxDepth( theTreeResult ) > 0 ) {
            this.rowsCount = 1;
            this.rowsPerPageOptions = [1, 2, 5];
            this.notificationService.info( 'Aus Performanzgründen wird die Anzeige auf Paginierung mit einer Ebene pro Seite umgeschaltet',
                'Achtung, die Anzahl der geöffneten Knoten überschreitet 1000' );
        } else {
            this.rowsCount = 10;
            this.rowsPerPageOptions = [10, 20, 50];
        }
        this._treeResult = theTreeResult;
    }

    rowsExpandedCount: number = 0;
    columnType = ColumnType;
    cols: Column[] = [];
    tableHeader: TableHeader | null = null;
    colSpanTotalsRow: number = 0;
    numberOfDisplayedRows = 20;
    totalsRow?: Row;
    showTotalsRow = true;
    multiSortMeta: any[] = [];
    sortMode: 'single' | 'multiple' = 'multiple';
    filterActive = false;

    @ViewChild( 'tt' ) table?: TreeTable;

    @HostListener( 'document:keydown.control' )
    onCtrlPress() {
        this.isCtrlPressed = true;
    }

    @HostListener( 'document:keyup.control' )
    onCtrlRelease() {
        this.isCtrlPressed = false;
    }

    // die Sortierung der Tabelle wurde mit "this.setSortFields(this.tableHeader);" einmal getriggert,
    // danach muss die initiale Sortierung mit "this.table.reset();" zurückgesetzt werden,
    // damit die treeTableSortIcons auch wieder in der Ausgangslage sind
    ngAfterViewInit(): void {
        // Defer the update until Angular finishes the current view check to avoid NG0100.
        queueMicrotask( () => {
            // Reset the temporary initial sorting in the PrimeNG table.
            this.table?.reset();

            // der Sortmode 'multiple' erzeugt unerwartetes Verhalten
            // bei leeren Zellen in einigen Spalten Ticket 321124
            this.sortMode = 'single';

            // Ensure that the deferred state change is rendered.
            this.cdRef.markForCheck();
        } );
    }


    ngOnChanges( changes: SimpleChanges ): void {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const headerOrderChange = changes['headerOrder'];
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const totalColumnChange = changes['totalColumn'];

        if ( headerOrderChange ) {
            this.rearrangeHeader( headerOrderChange.currentValue );
        }

        if ( totalColumnChange ) {
            this.setTotalColumn();
        }
    }

    setTotalColumn() {
        if ( this.flatResult === null ) {
            throw new Error( 'Cannot rearrange header. Report does not exist.' );
        }
        let totalColumns = this.flatResult?.columns.filter( col => col.isTotalColumn === true ) ?? [];
        for ( let totalColumn of totalColumns ) {
            totalColumn.hidden = !this.totalColumn;
        }
        this.rearrangeHeader( this.headerOrder );
        //this.headerRearrangerService.rearrangeHeader(this.result.headerOrder);
    }

    handleNewResult( result: Result ) {
        let numcols = result.columns.filter( col => this.isNumberCol( col ) );
        result.rows.forEach( row => {
            numcols.forEach( col => {
                row[col.field as keyof Row] = parseFloat( ( row[col.field as keyof Row] ? row[col.field as keyof Row] : 0 )
                    .toString().split( '.' ).join( '' ).replace( ',', '.' ) );
            } );
        } );
        this.flatResult = Result.resultFactory( result );
        this.cols = this.flatResult.columns;
        if ( this.flatResult.isBiAnalyse() ) {
            this.totalsRow = this.flatResult.getTotalRow();
            this.rearrangeHeader( result.headerOrder );
        }
        this.treeResult = this.flatResult.buildHierarchicalDataStructure();
        this.colSpanTotalsRow = this.flatResult.getAttributeColumns().length;
    }

    rearrangeHeader( headerOrder: string[] ) {
        this.flatResult.headerOrder = headerOrder;
        this.tableHeader = this.flatResult.generateHierarchyHeader();
        let numberOfHeaderRows = this.tableHeader.headerRows.length;
        this.cols = this.flatResult.getAttributeColumns().concat(
            this.tableHeader.headerRows[numberOfHeaderRows - 1].headerElements.map( element => element.column ) )
            .filter( this.flatResult.onlyUnique );
        // Hierarchien, wie die Orgstruktur werden nur in einer Spalte angezeigt
        let levelColumns = this.flatResult.columns.filter( col => col.type === ColumnType.HierarchyLevelColumn );
        this.tableHeader.headerRows[0].headerElements = this.tableHeader.headerRows[0].headerElements
            .filter( col => !( levelColumns.find( levelCol => levelCol.header === col.caption ) ) );
        this.cols = this.cols.filter( col => col.type !== ColumnType.HierarchyLevelColumn );
        this.setSortFields( this.tableHeader );
    }

    columnsReorderedFromTemplate( event: any ) {
        let columnOrder: Column[] = event.columns;
        const columns = columnOrder.filter( col => col.groupable );
        if ( this.flatResult.isBiAnalyse() ) {
            let uniqueFields = new Set<string>( columnOrder.map( c => c.field ) );
            this.leftDimensionAttributeChange.emit( [...uniqueFields] );
        } else { // rebuild tree hierarchy for 'Projektfinanzbericht'
            this.rebuildHierarchy( columnOrder, columns );
            return;
        }
    }

    reorderingAllowed() {
        return !this.readOnly;
    }

    private rebuildHierarchy( columnOrder: Column[], groupableColumns: Column[] ) {
        this.flatResult.columns = this.insertLevelColumns( columnOrder );
        this.treeResult = this.flatResult.buildHierarchicalDataStructure();
        if ( this.flatResult.isBiAnalyse() ) {
            this.tableHeader = this.flatResult.generateHierarchyHeader( groupableColumns );
        }
    }

    // Die Ebenenspalten von hierarchischen Tabellen müssen mit der Hauptebene mitverschoben werden,
    // damit die Reihenfolge der Spalten zum Gruppieren weiterhin passt.
    private insertLevelColumns( columnOrder: Column[] ): Column[] {
        let cloneColumnOrder = cloneDeep( columnOrder );
        let levelColumns = this.flatResult.columns.filter( col => col.type === ColumnType.HierarchyLevelColumn );
        if ( levelColumns.length > 0 ) {
            let indexOfMainLevelColumn = cloneColumnOrder.findIndex( col => col.field === levelColumns[0].refColumnField );
            cloneColumnOrder.splice( indexOfMainLevelColumn + 1, 0, ...levelColumns );
        }
        return cloneColumnOrder;
    }

    isNumberCol( col: Column ): boolean {
        return col.type === ColumnType.IntegerColumn || col.type === ColumnType.DecimalColumn;
    }

    setSortFields( tableHeader: TableHeader ) {
        this.sortMode = 'multiple';
        let index = 1;
        this.multiSortMeta = [];
        for ( let headerElement of tableHeader.headerRows[0].headerElements ) {
            if ( headerElement.column.groupable ) {
                this.multiSortMeta.push( { field: headerElement.column.refColumnField ?? headerElement.column.field, order: index } );
                index++;
            }
        }
    }

    toggleTree( state: 'expand' | 'collapse' ) {
        const newTreeResult = cloneDeep( this.treeResult );
        newTreeResult.forEach( ( node: TreeNode ) => {
            this.adjust( node, state === 'expand' ? true : false );
        } );
        this.treeResult = newTreeResult;
    }

    adjust( node: TreeNode, state: boolean ) {
        node.expanded = state;
        if ( node.children ) {
            node.children.forEach( childNode => {
                this.adjust( childNode, state );
            } );
        }
    }

    onNodeExpand( event: any ) {
        if ( this.isCtrlPressed && event.node.children ) {
            this.expandChildren( event.node );
        }
        this.treeResult = [...this.treeResult];
    }

    onNodeCollapse( event: any ) {
        this.treeResult = [...this.treeResult];
    }

    expandChildren( node: TreeNode ) {
        node.expanded = true;
        if ( node.children ) {
            for ( let child of node.children ) {
                this.expandChildren( child );
            }
        }
    }

    countExpandedNodes( nodes: TreeNode[] ): number {
        let count = 0;
        if ( !nodes ) return count;

        nodes.forEach( node => {
            count++;
            if ( node.expanded && node.children ) {
                count += this.countExpandedNodes( node.children );
            }
        } );
        return count;
    }

    maxDepth( nodes: TreeNode[], d = 0 ): number {
        let nextd = d + 1;
        nodes.forEach( node => {
            if ( node.children && node.children.length > 0 ) {
                d = Math.max( this.maxDepth( node.children, nextd ) );
            }
        } );
        return d;
    }


}
